import {useLocation, useNavigate} from "react-router-dom";
import application from "../../core/application";
import React, {useEffect, useState} from "react";
import "./shell.page.scss";
import {
  AppBar,
  Avatar,
  Backdrop,
  Box,
  Chip,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  SpeedDial,
  SpeedDialAction,
  SpeedDialIcon,
  Toolbar,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import {
  Business as BusinessIcon,
  Category as CategoryIcon,
  Home,
  Inventory as InventoryIcon,
  Logout as LogoutIcon,
  Person as PersonIcon,
  Settings as SettingsIcon,
  Payments as PaymentsIcon,
  Storefront as StorefrontIcon,
  BarChart as BarChartIcon,
  Percent as PercentIcon,
  Inventory2Outlined as Inventory2OutlinedIcon,
  PointOfSale as PointOfSaleIcon,
  ReceiptLong as ReceiptLongIcon,
  PeopleAlt as PeopleAltIcon,
  AutoAwesomeOutlined as BulkProductsIcon,
  Assessment as AssessmentIcon,
  MenuBook as MenuBookIcon,
  HeadsetMic as HeadsetMicIcon,
} from "@mui/icons-material";
import {Permission} from "../../users/user.entity";
import authenticationService from "../../authentication/authentication.service";
import {APP_DRAWER_WIDTH} from "../layout-constants";
import {analyticsService} from "../../core/analytics";

const drawerWidth = APP_DRAWER_WIDTH;

const drawerItemSx = {
  borderRadius: 2,
  mx: 1,
  mb: 0.25,
  "&.Mui-selected": {
    bgcolor: "rgba(45, 212, 191, 0.16)",
    "&:hover": {
      bgcolor: "rgba(45, 212, 191, 0.22)",
    },
  },
  "&:hover": {
    bgcolor: "rgba(255,255,255,0.06)",
  },
};

const drawerSubheaderSx = {
  bgcolor: "transparent",
  color: "rgba(248, 250, 252, 0.42)",
  fontSize: 10,
  fontWeight: 700,
  letterSpacing: "0.14em",
  textTransform: "uppercase" as const,
  lineHeight: 1.2,
  py: 1.25,
  pl: 2,
  pr: 2,
  mt: 0.5,
};

type ShellProps = {
  title: string;
  children: React.ReactNode;
  speedDial?: Array<{
    title: string;
    icon: JSX.Element;
    color:
      | "inherit"
      | "default"
      | "primary"
      | "secondary"
      | "error"
      | "info"
      | "success"
      | "warning";
    tooltipOpen?: boolean;
    onClick: () => void;
  }>;
  speedDialOnlySize?: number;
};

const pathMatches = (pathname: string, prefix: string) => {
  if (prefix === "/painel") return pathname === "/painel";
  return pathname === prefix || pathname.startsWith(`${prefix}/`);
};

const isProductsMainRoute = (pathname: string) => {
  if (pathname === "/produtos") return true;
  if (pathname.startsWith("/produtos/novo")) return true;
  if (pathname.startsWith("/produtos/editar/")) return true;
  if (pathname.startsWith("/produtos/qrcode/")) return true;
  return false;
};

export const Shell = (props: ShellProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [user] = application.user.useState();
  const [bussiness] = application.bussiness.useState();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [speedDialOpened, setSpeedDialOpened] = useState(false);

  const theme = useTheme();
  const displaySpeedDial = useMediaQuery(
    theme.breakpoints.down(props.speedDialOnlySize || 0)
  );

  useEffect(() => {
    if (!user) navigate("/login");
  }, [navigate, user]);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const canSell = user?.hasPermission(Permission.SELL);
  const canSalesStats = user?.hasPermission([
    Permission.SELL,
    Permission.READ_SALES_ANALYTICS,
  ]);
  const canProducts = user?.hasPermission([
    Permission.SELL,
    Permission.WRITE_PRODUCTS,
  ]);
  const canBulkProducts = user?.hasPermission(Permission.WRITE_PRODUCTS);
  const canAdmin = user?.hasPermission(Permission.ADMIN);
  const canCashRegister = user?.hasPermission(Permission.CASH_REGISTER_MANAGEMENT);
  const canPayables = user?.hasPermission(Permission.ACCOUNTS_PAYABLE);
  const canManageCustomers = user?.hasPermission(Permission.MANAGE_CUSTOMERS);
  const showVendasGroup = canSalesStats || canAdmin;
  const showProdutosGroup = canProducts;
  const showAdminGroup = canAdmin;

  const closeMobile = () => setMobileOpen(false);

  const drawerPaperSx = {
    boxSizing: "border-box" as const,
    width: drawerWidth,
    zIndex: theme.zIndex.drawer,
    bgcolor: "#0f172a",
    color: "#f8fafc",
    borderRight: "1px solid rgba(148, 163, 184, 0.12)",
    backgroundImage:
      "linear-gradient(180deg, rgba(15,23,42,1) 0%, rgba(15,23,42,0.97) 100%)",
  };

  const drawer = (
    <Box sx={{height: "100%", display: "flex", flexDirection: "column"}}>
      <Toolbar
        sx={{
          px: 2,
          minHeight: 72,
          alignItems: "flex-start",
          justifyContent: "center",
          flexDirection: "column",
          gap: 0.5,
        }}
      >
        <Typography
          variant="subtitle2"
          sx={{
            color: "rgba(248,250,252,0.55)",
            textTransform: "uppercase",
            letterSpacing: "0.12em",
            fontSize: 11,
            fontWeight: 600,
          }}
        >
          Painel
        </Typography>
        <Typography
          variant="h6"
          sx={{
            fontWeight: 700,
            letterSpacing: "-0.02em",
            lineHeight: 1.25,
            color: "#f8fafc",
          }}
        >
          {bussiness?.name ?? "Simplou"}
        </Typography>
      </Toolbar>
      <Divider sx={{borderColor: "rgba(148, 163, 184, 0.15)"}} />
      <List sx={{px: 0.5, pt: 1, flex: 1}}>
        <ListSubheader
          component="div"
          disableSticky
          sx={{...drawerSubheaderSx, mt: 0, pt: 0.5}}
        >
          Principal
        </ListSubheader>
        <ListItem disablePadding>
          <ListItemButton
            selected={pathMatches(location.pathname, "/painel")}
            onClick={() => {
              navigate("/painel");
              closeMobile();
            }}
            sx={drawerItemSx}
          >
            <ListItemIcon sx={{color: "inherit", minWidth: 40}}>
              <Home fontSize="small" />
            </ListItemIcon>
            <ListItemText
              primary="Início"
              primaryTypographyProps={{fontWeight: 600, fontSize: 14}}
            />
          </ListItemButton>
        </ListItem>

        {canSell ? (
          <ListItem disablePadding>
            <ListItemButton
              selected={pathMatches(location.pathname, "/vender")}
              onClick={() => {
                navigate("/vender");
                closeMobile();
              }}
              sx={drawerItemSx}
            >
              <ListItemIcon sx={{color: "inherit", minWidth: 40}}>
                <StorefrontIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText
                primary="Vender"
                primaryTypographyProps={{fontWeight: 600, fontSize: 14}}
              />
            </ListItemButton>
          </ListItem>
        ) : null}

        {showVendasGroup ? (
          <>
            <ListSubheader component="div" disableSticky sx={drawerSubheaderSx}>
              Vendas e relatórios
            </ListSubheader>
            {canSalesStats ? (
              <ListItem disablePadding>
                <ListItemButton
                  selected={location.pathname.startsWith("/vendas")}
                  onClick={() => {
                    navigate("/vendas/estatisticas");
                    closeMobile();
                  }}
                  sx={drawerItemSx}
                >
                  <ListItemIcon sx={{color: "inherit", minWidth: 40}}>
                    <BarChartIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Estatísticas e relatórios"
                    primaryTypographyProps={{fontWeight: 600, fontSize: 14}}
                  />
                </ListItemButton>
              </ListItem>
            ) : null}
            {canCashRegister ? (
              <ListItem disablePadding>
                <ListItemButton
                  selected={pathMatches(location.pathname, "/caixa")}
                  onClick={() => {
                    navigate("/caixa");
                    closeMobile();
                  }}
                  sx={drawerItemSx}
                >
                  <ListItemIcon sx={{color: "inherit", minWidth: 40}}>
                    <PointOfSaleIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Caixa"
                    primaryTypographyProps={{fontWeight: 600, fontSize: 14}}
                  />
                </ListItemButton>
              </ListItem>
            ) : null}
            {canAdmin ? (
              <ListItem disablePadding>
                <ListItemButton
                  selected={pathMatches(location.pathname, "/comissoes")}
                  onClick={() => {
                    navigate("/comissoes");
                    closeMobile();
                  }}
                  sx={drawerItemSx}
                >
                  <ListItemIcon sx={{color: "inherit", minWidth: 40}}>
                    <PercentIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Comissões"
                    primaryTypographyProps={{fontWeight: 600, fontSize: 14}}
                  />
                </ListItemButton>
              </ListItem>
            ) : null}
          </>
        ) : null}

        {canPayables || canManageCustomers ? (
          <>
            <ListSubheader component="div" disableSticky sx={drawerSubheaderSx}>
              Financeiro
            </ListSubheader>
            {canManageCustomers ? (
              <>
              <ListItem disablePadding>
                <ListItemButton
                  selected={pathMatches(location.pathname, "/clientes")}
                  onClick={() => {
                    navigate("/clientes");
                    closeMobile();
                  }}
                  sx={drawerItemSx}
                >
                  <ListItemIcon sx={{color: "inherit", minWidth: 40}}>
                    <PeopleAltIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Clientes"
                    primaryTypographyProps={{fontWeight: 600, fontSize: 14}}
                  />
                </ListItemButton>
              </ListItem>
              <ListItem disablePadding>
                <ListItemButton
                  selected={pathMatches(location.pathname, "/clientes/relatorio")}
                  onClick={() => {
                    navigate("/clientes/relatorio");
                    closeMobile();
                  }}
                  sx={drawerItemSx}
                >
                  <ListItemIcon sx={{color: "inherit", minWidth: 40}}>
                    <AssessmentIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Relatório de Fiado"
                    primaryTypographyProps={{fontWeight: 600, fontSize: 14}}
                  />
                </ListItemButton>
              </ListItem>
              </>
            ) : null}
            {canPayables ? (
            <ListItem disablePadding>
              <ListItemButton
                selected={pathMatches(location.pathname, "/contas-a-pagar")}
                onClick={() => {
                  navigate("/contas-a-pagar");
                  closeMobile();
                }}
                sx={drawerItemSx}
              >
                <ListItemIcon sx={{color: "inherit", minWidth: 40}}>
                  <ReceiptLongIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary="Contas a pagar"
                  primaryTypographyProps={{fontWeight: 600, fontSize: 14}}
                />
              </ListItemButton>
            </ListItem>
            ) : null}
          </>
        ) : null}

        {showProdutosGroup ? (
          <>
            <ListSubheader component="div" disableSticky sx={drawerSubheaderSx}>
              Catálogo
            </ListSubheader>
            <ListItem disablePadding>
              <ListItemButton
                selected={isProductsMainRoute(location.pathname)}
                onClick={() => {
                  navigate("/produtos");
                  closeMobile();
                }}
                sx={drawerItemSx}
              >
                <ListItemIcon sx={{color: "inherit", minWidth: 40}}>
                  <InventoryIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary="Produtos"
                  primaryTypographyProps={{fontWeight: 600, fontSize: 14}}
                />
              </ListItemButton>
            </ListItem>
            {canBulkProducts ? (
              <ListItem disablePadding>
                <ListItemButton
                  selected={pathMatches(
                    location.pathname,
                    "/produtos/cadastro-em-massa"
                  )}
                  onClick={() => {
                    navigate("/produtos/cadastro-em-massa");
                    closeMobile();
                  }}
                  sx={drawerItemSx}
                >
                  <ListItemIcon sx={{color: "inherit", minWidth: 40}}>
                    <BulkProductsIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Cadastro em massa"
                    primaryTypographyProps={{fontWeight: 600, fontSize: 14}}
                  />
                </ListItemButton>
              </ListItem>
            ) : null}
            <ListItem disablePadding>
              <ListItemButton
                selected={pathMatches(
                  location.pathname,
                  "/produtos/categorias"
                )}
                onClick={() => {
                  navigate("/produtos/categorias");
                  closeMobile();
                }}
                sx={drawerItemSx}
              >
                <ListItemIcon sx={{color: "inherit", minWidth: 40}}>
                  <CategoryIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary="Categorias"
                  primaryTypographyProps={{fontWeight: 600, fontSize: 14}}
                />
              </ListItemButton>
            </ListItem>
            <ListItem disablePadding>
              <ListItemButton
                selected={pathMatches(
                  location.pathname,
                  "/produtos/fornecedores"
                )}
                onClick={() => {
                  navigate("/produtos/fornecedores");
                  closeMobile();
                }}
                sx={drawerItemSx}
              >
                <ListItemIcon sx={{color: "inherit", minWidth: 40}}>
                  <BusinessIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary="Fornecedores"
                  primaryTypographyProps={{fontWeight: 600, fontSize: 14}}
                />
              </ListItemButton>
            </ListItem>
            <ListItem disablePadding>
              <ListItemButton
                selected={pathMatches(location.pathname, "/produtos/estoque")}
                onClick={() => {
                  navigate("/produtos/estoque");
                  closeMobile();
                }}
                sx={drawerItemSx}
              >
                <ListItemIcon sx={{color: "inherit", minWidth: 40}}>
                  <Inventory2OutlinedIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary="Estoque"
                  primaryTypographyProps={{fontWeight: 600, fontSize: 14}}
                />
              </ListItemButton>
            </ListItem>
          </>
        ) : null}

        <>
          <ListSubheader component="div" disableSticky sx={drawerSubheaderSx}>
            Suporte e Ajuda
          </ListSubheader>
          <ListItem disablePadding>
            <ListItemButton
              selected={pathMatches(location.pathname, "/suporte/base-de-conhecimento")}
              onClick={() => {
                navigate("/suporte/base-de-conhecimento");
                closeMobile();
              }}
              sx={drawerItemSx}
            >
              <ListItemIcon sx={{color: "inherit", minWidth: 40}}>
                <MenuBookIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText
                primary="Base de Conhecimento"
                primaryTypographyProps={{fontWeight: 600, fontSize: 14}}
              />
            </ListItemButton>
          </ListItem>
          <ListItem disablePadding>
            <ListItemButton
              selected={pathMatches(location.pathname, "/suporte/atendimento")}
              onClick={() => {
                navigate("/suporte/atendimento");
                closeMobile();
              }}
              sx={drawerItemSx}
            >
              <ListItemIcon sx={{color: "inherit", minWidth: 40}}>
                <HeadsetMicIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText
                primary="Atendimento"
                primaryTypographyProps={{fontWeight: 600, fontSize: 14}}
              />
            </ListItemButton>
          </ListItem>
        </>

        {showAdminGroup ? (
          <>
            <ListSubheader component="div" disableSticky sx={drawerSubheaderSx}>
              Administração
            </ListSubheader>
            <ListItem disablePadding>
              <ListItemButton
                selected={pathMatches(location.pathname, "/usuarios")}
                onClick={() => {
                  navigate("/usuarios");
                  closeMobile();
                }}
                sx={drawerItemSx}
              >
                <ListItemIcon sx={{color: "inherit", minWidth: 40}}>
                  <PersonIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary="Usuários"
                  primaryTypographyProps={{fontWeight: 600, fontSize: 14}}
                />
              </ListItemButton>
            </ListItem>
            <ListItem disablePadding>
              <ListItemButton
                selected={pathMatches(location.pathname, "/meu-plano")}
                onClick={() => {
                  navigate("/meu-plano");
                  closeMobile();
                }}
                sx={drawerItemSx}
              >
                <ListItemIcon sx={{color: "inherit", minWidth: 40}}>
                  <PaymentsIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary="Meu Plano"
                  primaryTypographyProps={{fontWeight: 600, fontSize: 14}}
                />
              </ListItemButton>
            </ListItem>
            <ListItem disablePadding>
              <ListItemButton
                selected={pathMatches(location.pathname, "/configuracoes")}
                onClick={() => {
                  navigate("/configuracoes");
                  closeMobile();
                }}
                sx={drawerItemSx}
              >
                <ListItemIcon sx={{color: "inherit", minWidth: 40}}>
                  <SettingsIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary="Configurações"
                  primaryTypographyProps={{fontWeight: 600, fontSize: 14}}
                />
              </ListItemButton>
            </ListItem>
          </>
        ) : null}
      </List>

      <Divider sx={{borderColor: "rgba(148, 163, 184, 0.15)"}} />
      <List sx={{px: 0.5, py: 1}}>
        <ListItem disablePadding>
          <ListItemButton
            onClick={async () => {
              analyticsService.trackEvent("logout_click");
              await authenticationService.logout();
              navigate("/login");
              closeMobile();
            }}
            sx={{
              ...drawerItemSx,
              color: "rgba(248, 250, 252, 0.85)",
            }}
          >
            <ListItemIcon sx={{color: "inherit", minWidth: 40}}>
              <LogoutIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText
              primary="Sair"
              primaryTypographyProps={{fontWeight: 600, fontSize: 14}}
            />
          </ListItemButton>
        </ListItem>
      </List>
    </Box>
  );

  const container =
    typeof window !== "undefined" ? () => window.document.body : undefined;

  const userInitial = user?.name?.charAt(0)?.toUpperCase() ?? "?";

  return (
    <Box sx={{display: "flex", minHeight: "100vh"}}>
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          width: {md: `calc(100% - ${drawerWidth}px)`},
          ml: {md: `${drawerWidth}px`},
          bgcolor: "background.paper",
          color: "text.primary",
          borderBottom: 1,
          borderColor: "divider",
        }}
      >
        <Toolbar sx={{minHeight: 64, gap: 2}}>
          <IconButton
            color="inherit"
            aria-label="abrir menu"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{mr: 1, display: {md: "none"}}}
          >
            <MenuIcon />
          </IconButton>
          <Typography
            variant="h6"
            noWrap
            component="div"
            sx={{flexGrow: 1, fontWeight: 700, letterSpacing: "-0.02em"}}
          >
            {props.title}
          </Typography>
          {user ? (
            <Chip
              avatar={
                <Avatar
                  sx={{
                    bgcolor: "primary.main",
                    color: "primary.contrastText",
                    width: 28,
                    height: 28,
                    fontSize: "0.85rem",
                  }}
                >
                  {userInitial}
                </Avatar>
              }
              label={user.name}
              variant="outlined"
              sx={{
                borderColor: "divider",
                fontWeight: 600,
                "& .MuiChip-label": {px: 1.5},
              }}
            />
          ) : null}
        </Toolbar>
      </AppBar>
      <Box
        component="nav"
        sx={{width: {md: drawerWidth}, flexShrink: {md: 0}}}
        aria-label="Navegação principal"
      >
        <Drawer
          container={container}
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: {xs: "block", md: "none"},
            "& .MuiDrawer-paper": drawerPaperSx,
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: {xs: "none", md: "block"},
            "& .MuiDrawer-paper": drawerPaperSx,
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          minWidth: 0,
          overflowX: "hidden",
          p: {xs: 2, sm: 3},
          width: {md: `calc(100% - ${drawerWidth}px)`},
          bgcolor: "background.default",
          minHeight: "100vh",
        }}
      >
        <Toolbar />
        <Box sx={{maxWidth: 1200, mx: "auto"}}>{props.children}</Box>
      </Box>

      {props.speedDial &&
      (!props.speedDialOnlySize ||
        (props.speedDialOnlySize && displaySpeedDial)) ? (
        <>
          <Backdrop
            open={speedDialOpened}
            sx={{zIndex: (t) => t.zIndex.drawer + 10}}
          />
          <SpeedDial
            ariaLabel="Ações"
            sx={{
              position: "fixed",
              bottom: 24,
              right: 24,
              zIndex: (t) => t.zIndex.drawer + 20,
            }}
            icon={<SpeedDialIcon />}
            onClose={() => setSpeedDialOpened(false)}
            onOpen={() => setSpeedDialOpened(true)}
            open={speedDialOpened}
          >
            {props.speedDial.map((dial) => (
              <SpeedDialAction
                key={dial.title}
                icon={dial.icon}
                tooltipTitle={dial.title}
                FabProps={{
                  color: dial.color,
                }}
                tooltipOpen={dial.tooltipOpen || false}
                onClick={() => {
                  setSpeedDialOpened(false);
                  dial.onClick();
                }}
              />
            ))}
          </SpeedDial>
        </>
      ) : null}
    </Box>
  );
};
